/**
 * Vehicle pool + kinematic update — Stage 1b.
 *
 * Two populations share one pool structure but move differently:
 *
 * - **Threat** vehicles (inside corridor, oncoming). They move +Z at their
 *   live closing speed = current player speed + the vehicle's absolute speed.
 *   The player is fixed at z=0, so a threat approaching the player simply
 *   increases its z over time. Despawn when z > TUNING.traffic.despawnBehind
 *   (past the player) and recycle the slot, emitting a `despawn` event.
 *
 * - **Ambient** vehicles (outside guardrails, scenic, non-collidable). They
 *   carry a signed ABSOLUTE road speed (+ = oncoming, - = same direction as
 *   the player) and are integrated in the player frame exactly like threats,
 *   at `playerSpeed + _absSpeed`. That keeps them moving correctly relative to
 *   the road as the player-speed ramp climbs (a 20 m/s same-direction car must
 *   fall BEHIND a 30 m/s player, not race away forever), and it makes
 *   same-direction ambients recycle in seconds instead of pinning pool slots.
 *   Ambient never collides.
 *
 * Kinematic only: no Rapier in this stage. `motion === "driving"` for all
 * spawned vehicles; the kicked state + body sync lands in Stage 1c.
 *
 * Zero per-frame allocation: the pool pre-allocates `maxThreat + maxAmbient`
 * `VehicleEntity` instances. `spawnVehicle` fills a recycled slot in place;
 * `updateVehicles` mutates fields in place. Events are pushed onto the
 * caller-provided `SimEvent[]` queue (the queue is pre-sized by the caller).
 *
 * Field semantics:
 * - `VehicleEntity.closingSpeed` (public, per types.ts) = v_p + v_veh at
 *   spawn, i.e. the spawn-time closing speed. Used by spawn-distance math and
 *   tests. It is NOT used to integrate position once the player speed ramps.
 * - `_absSpeed` (internal, this module only) = the vehicle's SIGNED absolute
 *   road speed: positive = travelling toward the player (oncoming), negative =
 *   travelling the same direction as the player. Threats are always oncoming;
 *   ambients may be either. Every live vehicle integrates at
 *   `currentPlayerSpeed + _absSpeed`, so the dual-speed ramp is honoured for
 *   both populations.
 */

import type {
  CarModel,
  SimEvent,
  TruckModel,
  VehicleEntity,
  VehicleKind,
  VehicleModel,
  VehicleRole,
} from "./types";
import { TUNING } from "./tuning";

const CT = TUNING.contact;

/** Internal liveness + absolute-speed fields layered onto VehicleEntity. */
interface VehicleSlot extends VehicleEntity {
  /** True while this slot holds a live spawned entity. */
  _alive: boolean;
  /** Absolute vehicle speed (threats: v_veh; ambients: signed frame v). */
  _absSpeed: number;
}

/** Lateral center of corridor lane index 0..2 (corridor centered on x=0). */
export function laneCenterX(lane: number): number {
  // lane 0 -> -2.7, lane 1 -> 0, lane 2 -> +2.7 (arcade lane width)
  return (lane - 1) * TUNING.road.laneWidth;
}

/** Vehicle bounding length by kind (m). */
export function vehicleLength(kind: VehicleKind): number {
  return kind === "truck" ? TUNING.traffic.truckLength : TUNING.traffic.carLength;
}

/** Vehicle bounding width by kind (m). */
export function vehicleWidth(kind: VehicleKind): number {
  return kind === "truck" ? TUNING.traffic.truckWidth : TUNING.traffic.carWidth;
}

// ---------------------------------------------------------------------------
// Per-model dimensions + deterministic model selection.
//
// The sim owns model choice so its colliders and crash extents can use the
// dimensions of the model the player actually SEES. The hash is the one the
// render previously used (assets.ts pickVehicleModel), keyed on the pool slot
// id, so the visual fleet is byte-for-byte unchanged — only the authority for
// the choice moved.
// ---------------------------------------------------------------------------

const CAR_MODELS: readonly CarModel[] = ["sedan", "suv", "hatchback"];
const TRUCK_MODELS: readonly TruckModel[] = ["boxtruck", "semi"];

/** Integer hash → [0,1). Must match the historical render-side hash exactly. */
function hash01(n: number): number {
  let h = (n * 2654435761) >>> 0;
  h ^= h >>> 15;
  h = Math.imul(h, 2246822519);
  h ^= h >>> 13;
  return (h >>> 0) / 4294967296;
}

/** Pick a car model deterministically from a vehicle id (stable within a run). */
export function pickCarModel(vehicleId: number): CarModel {
  return CAR_MODELS[Math.floor(hash01(vehicleId) * CAR_MODELS.length)];
}

/** Pick a truck model deterministically from a vehicle id (stable within a run). */
export function pickTruckModel(vehicleId: number): TruckModel {
  return TRUCK_MODELS[Math.floor(hash01(vehicleId) * TRUCK_MODELS.length)];
}

/** Pick a vehicle model for a sim kind from a vehicle id (pure, deterministic). */
export function pickVehicleModel(kind: VehicleKind, vehicleId: number): VehicleModel {
  return kind === "truck" ? pickTruckModel(vehicleId) : pickCarModel(vehicleId);
}

/**
 * Per-SPAWN paint draw in [0,1) for `VehicleEntity.paint`.
 *
 * Keyed on (slot id, spawn serial) rather than the slot id alone: the serial is
 * the pool's monotonically increasing spawn counter, so the SAME slot coming
 * back for its second, third, … occupant draws a different colour every time.
 * Deterministic — a run replays identically for the same seed because the
 * serial is a pure function of the spawn sequence, and `resetVehiclePool`
 * rewinds it to 0.
 */
export function paintDraw(vehicleId: number, spawnSerial: number): number {
  return hash01(vehicleId * 7919 + spawnSerial * 104729 + 1);
}

/** Real-world bounding length of a MODEL (m) — matches the rendered GLB. */
export function modelLength(model: VehicleModel): number {
  return TUNING.vehicleModels[model].length;
}

/** Real-world bounding width of a MODEL (m) — matches the rendered GLB. */
export function modelWidth(model: VehicleModel): number {
  return TUNING.vehicleModels[model].width;
}

/** Real-world bounding height of a MODEL (m) — matches the rendered GLB. */
export function modelHeight(model: VehicleModel): number {
  return TUNING.vehicleModels[model].height;
}

// ---------------------------------------------------------------------------
// Contact extents — the single source of truth for "are the player and this
// vehicle touching?", shared by crash detection and near-miss scoring so the
// two can never disagree or overlap.
// ---------------------------------------------------------------------------

/**
 * Lateral half-distance at which the visible vehicle and the visible bike
 * touch: the sum of their visual half-widths, minus the arcade forgiveness
 * margin (the hitbox is deliberately NARROWER than the art, never wider).
 *
 * A pass with `|v.x - px|` below this is a crash; above it, the surface gap is
 * `|v.x - px| - contactHalfWidth` and a gap in (0, nearMissMargin] scores a
 * near miss. The two bands are therefore adjacent and cannot overlap.
 */
export function contactHalfWidth(model: VehicleModel): number {
  return modelWidth(model) / 2 + CT.playerHalfWidth - CT.forgiveness;
}

/** Surface-to-surface lateral gap (m) between a vehicle and the player. Negative ⇒ overlap. */
export function lateralGap(v: VehicleEntity, playerX: number): number {
  return Math.abs(v.x - playerX) - contactHalfWidth(v.model);
}

/**
 * Whether the vehicle's visible body overlaps the visible bike.
 *
 * Longitudinal: the bike is NOT a point at z = 0 — it is drawn from the front
 * wheel plane (`contact.playerFrontZ`, -1.45 m, the camera looks over it) back
 * to `contact.playerBackZ`. The vehicle's near face is `v.z + halfLength`, so
 * contact begins the moment that face reaches the front wheel — which is what
 * the player sees — rather than when it reaches the camera plane.
 *
 * Lateral: `contactHalfWidth` above, from the visible model's own width.
 */
export function overlapsPlayer(v: VehicleEntity, playerX: number): boolean {
  if (!overlapsPlayerZ(v)) return false;
  return Math.abs(v.x - playerX) < contactHalfWidth(v.model);
}

/**
 * The LONGITUDINAL half of `overlapsPlayer`: the vehicle's visible box spans
 * the bike's z-window (front wheel plane .. rider back) right now, whatever the
 * lateral offset.
 *
 * Split out because the SQUEEZE rule (world.ts `checkCrash`) needs to know
 * which vehicles are alongside the player at this instant: a player threading
 * the ~0.5 m corridor between two adjacent-lane vehicles clears both lateral
 * bands individually, so the only way to catch it is to test the pair — and
 * that test is only meaningful for vehicles that share the player's z-window.
 */
export function overlapsPlayerZ(v: VehicleEntity): boolean {
  const halfLen = modelLength(v.model) / 2;
  if (v.z + halfLen <= CT.playerFrontZ) return false; // still fully ahead
  if (v.z - halfLen >= CT.playerBackZ) return false; // fully past
  return true;
}

/**
 * Pooled vehicle store. Slot occupancy is determined by the internal `_alive`
 * flag; a dead slot is a candidate for recycling. Threats occupy the first
 * `maxThreat` slots, ambients the remaining `maxAmbient` slots.
 */
export interface VehiclePool {
  /** All slots; threats first [0,maxThreat), then ambients. */
  readonly slots: VehicleEntity[];
  /** Number of threat slots (first segment of `slots`). */
  readonly maxThreat: number;
  /** Number of ambient slots (second segment of `slots`). */
  readonly maxAmbient: number;
  /** Round-robin scan hint for the next free threat slot. */
  threatScanFrom: number;
  /** Round-robin scan hint for the next free ambient slot (absolute index). */
  ambientScanFrom: number;
  /**
   * Monotonic count of spawns since the pool was created/reset. Feeds the
   * per-spawn paint draw (see `paintDraw`) so a recycled slot never comes back
   * in the same colour. Rewound by `resetVehiclePool` for determinism.
   */
  spawnSerial: number;
}

function makeBlankSlot(id: number): VehicleSlot {
  return {
    id,
    kind: "car",
    model: pickVehicleModel("car", id),
    role: "ambient",
    motion: "driving",
    x: 0,
    y: 0,
    z: 0,
    yaw: 0,
    quat: null,
    paint: paintDraw(id, 0),
    closingSpeed: 0,
    lane: 0,
    nearMissAwarded: false,
    kickedAt: null,
    wreckFade: 0,
    _alive: false,
    _absSpeed: 0,
  };
}

/**
 * Create a vehicle pool with `maxThreat` threat slots and `maxAmbient` ambient
 * slots, all pre-allocated and free. Slot ids are stable for the pool's
 * lifetime: threat ids [0..maxThreat), ambient ids [maxThreat..total).
 */
export function createVehiclePool(maxThreat: number, maxAmbient: number): VehiclePool {
  const total = maxThreat + maxAmbient;
  const slots: VehicleEntity[] = new Array(total);
  for (let i = 0; i < total; i++) {
    slots[i] = makeBlankSlot(i);
  }
  return {
    slots,
    maxThreat,
    maxAmbient,
    threatScanFrom: 0,
    ambientScanFrom: maxThreat,
    spawnSerial: 0,
  };
}

/**
 * Rewind a pool to its just-constructed state: every slot dead, both
 * round-robin scan cursors back at their initial values.
 *
 * The cursors matter for determinism, not just tidiness: `spawnVehicle` derives
 * the visual+collider MODEL from the slot index, so a pool that starts a new run
 * mid-cycle hands out different models (and therefore different hitboxes) than a
 * freshly constructed one for the same seed. `World.reset()` calls this so a
 * reset run is byte-identical to a fresh world with the same seed.
 *
 * Emits a `despawn` event for each slot that was live.
 */
export function resetVehiclePool(pool: VehiclePool, events: SimEvent[]): void {
  const slots = pool.slots;
  for (let i = 0; i < slots.length; i++) {
    const v = slots[i] as VehicleSlot;
    if (v._alive) {
      v.quat = null;
      despawnVehicle(pool, v, events);
    }
  }
  pool.threatScanFrom = 0;
  pool.ambientScanFrom = pool.maxThreat;
  pool.spawnSerial = 0;
}

/** Whether a slot currently holds a live (spawned) entity. */
export function isLive(v: VehicleEntity): boolean {
  return (v as VehicleSlot)._alive;
}

/** Internal absolute speed of a vehicle (threats: v_veh; ambients: frame v). */
export function vehicleAbsSpeed(v: VehicleEntity): number {
  return (v as VehicleSlot)._absSpeed;
}

/** Find a free threat slot, or -1 if exhausted. Round-robin from scan hint. */
function findFreeThreat(pool: VehiclePool): number {
  const { slots, maxThreat } = pool;
  for (let i = 0; i < maxThreat; i++) {
    const idx = (pool.threatScanFrom + i) % maxThreat;
    if (!(slots[idx] as VehicleSlot)._alive) {
      pool.threatScanFrom = (idx + 1) % maxThreat;
      return idx;
    }
  }
  return -1;
}

/** Find a free ambient slot, or -1 if exhausted. */
function findFreeAmbient(pool: VehiclePool): number {
  const { slots, maxThreat, maxAmbient } = pool;
  const base = maxThreat;
  for (let i = 0; i < maxAmbient; i++) {
    const idx = base + ((pool.ambientScanFrom - base + i) % maxAmbient);
    if (!(slots[idx] as VehicleSlot)._alive) {
      pool.ambientScanFrom = (idx + 1 - base) % maxAmbient + base;
      return idx;
    }
  }
  return -1;
}

/** Initialization payload for a new vehicle. */
export interface VehicleInit {
  kind: VehicleKind;
  role: VehicleRole;
  lane: number;
  x: number;
  z: number;
  /**
   * The vehicle's SIGNED ABSOLUTE road speed (m/s): positive = oncoming
   * (travelling toward the player), negative = travelling the same direction as
   * the player. Threats are always positive; ambients may be either. The
   * spawn-time closing speed (v_p + speed) is stored in
   * `VehicleEntity.closingSpeed`.
   */
  speed: number;
  /** Current player forward speed (m/s) — needed to compute closing speed. */
  playerSpeed: number;
  yaw: number;
}

/**
 * Spawn a vehicle into a free slot of the matching role. Mutates the slot in
 * place. Returns the spawned entity, or `null` if the pool segment for that
 * role is exhausted (pool-exhaustion safety: never overwrites a live slot).
 * Emits a `spawn` event onto `events` on success.
 */
export function spawnVehicle(
  pool: VehiclePool,
  init: VehicleInit,
  events: SimEvent[],
): VehicleEntity | null {
  const idx = init.role === "threat" ? findFreeThreat(pool) : findFreeAmbient(pool);
  if (idx < 0) return null;
  const v = pool.slots[idx] as VehicleSlot;
  v.kind = init.kind;
  // Model choice is deterministic on the (stable) slot id, so a slot keeps the
  // same car/truck variant across recycles and the render never has to guess.
  v.model = pickVehicleModel(init.kind, idx);
  v.role = init.role;
  v.motion = "driving";
  v.x = init.x;
  v.y = 0;
  v.z = init.z;
  v.yaw = init.yaw;
  v.quat = null;
  // Paint is drawn PER SPAWN (not per slot): see `paintDraw`.
  v.paint = paintDraw(idx, pool.spawnSerial++);
  v._absSpeed = init.speed;
  // closingSpeed (public) = spawn-time closing speed (player frame velocity).
  v.closingSpeed = init.playerSpeed + init.speed;
  v.lane = init.lane;
  v.nearMissAwarded = false;
  v.kickedAt = null;
  v.wreckFade = 0;
  v._alive = true;
  events.push({ type: "spawn", vehicleId: v.id, kind: v.kind, role: v.role });
  return v;
}

/**
 * Despawn a live vehicle: mark its slot free and emit a `despawn` event.
 */
export function despawnVehicle(pool: VehiclePool, v: VehicleEntity, events: SimEvent[]): void {
  void pool;
  const slot = v as VehicleSlot;
  if (!slot._alive) return;
  slot._alive = false;
  // A dead slot never blinks: clear the wreck countdown so a recycled slot can
  // never inherit a fade from its previous occupant.
  slot.wreckFade = 0;
  events.push({ type: "despawn", vehicleId: v.id });
}

/** Count of currently live threat vehicles. */
export function liveThreatCount(pool: VehiclePool): number {
  let n = 0;
  for (let i = 0; i < pool.maxThreat; i++) {
    if ((pool.slots[i] as VehicleSlot)._alive) n++;
  }
  return n;
}

/** Count of currently live ambient vehicles. */
export function liveAmbientCount(pool: VehiclePool): number {
  let n = 0;
  for (let i = pool.maxThreat; i < pool.slots.length; i++) {
    if ((pool.slots[i] as VehicleSlot)._alive) n++;
  }
  return n;
}

/**
 * Advance all live vehicles by `dt` seconds.
 *
 * Every live vehicle moves at its live player-frame velocity
 * `playerSpeed + _absSpeed` so the dual-speed director ramp is reflected for
 * threats AND ambients (player speed increases over time).
 *
 * Despawn any live vehicle whose z > TUNING.traffic.despawnBehind and recycle
 * its slot, emitting `despawn` events. Same-direction ambients pull AHEAD of
 * the player and are recycled once they pass TUNING.traffic.ambientDespawnAhead
 * metres ahead — well beyond the fog horizon, so the recycle is invisible and
 * the ambient segment keeps cycling for arbitrarily long runs.
 */
export function updateVehicles(
  pool: VehiclePool,
  dt: number,
  playerSpeed: number,
  events: SimEvent[],
): void {
  const slots = pool.slots;
  const despawnZ = TUNING.traffic.despawnBehind;

  // Threats: live closing speed = current player speed + absolute vehicle speed.
  for (let i = 0; i < pool.maxThreat; i++) {
    const v = slots[i] as VehicleSlot;
    if (!v._alive) continue;
    // A KICKED threat's pose is owned by Rapier (world.ts syncKickedBodies
    // overwrites x/y/z every step). Advancing it here wrote a bogus z that the
    // despawn test below then read, so a kicked vehicle could be despawned on
    // a z its body never had — one step of closing speed (~0.5-0.9 m) early,
    // and without the body reset that checkKickedLanded performs.
    if (v.motion === "kicked") continue;
    v.z += (playerSpeed + v._absSpeed) * dt;
    if (v.z > despawnZ) {
      despawnVehicle(pool, v, events);
    }
  }

  // Ambients: same player-frame integration, own signed absolute road speed.
  const ambientAheadZ = TUNING.traffic.ambientDespawnAhead;
  for (let i = pool.maxThreat; i < slots.length; i++) {
    const v = slots[i] as VehicleSlot;
    if (!v._alive) continue;
    v.z += (playerSpeed + v._absSpeed) * dt;
    if (v.z > despawnZ || v.z < -ambientAheadZ) {
      despawnVehicle(pool, v, events);
    }
  }
}
